import { useState } from 'react';

import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Modal, Progress } from '@atom/mui';
import {
  CloseIcon,
  DoneAllIcon,
  ErrorOutlineIcon,
  type IconComponent,
  InfoIcon,
  SearchOffIcon,
  WorkIcon,
} from '@atom/mui/icons';
import { AssetDetail } from '@atom/types/asset';
import { PolicyAction } from '@atom/types/policy';
import { AttributesType } from '@atom/types/schema';
import { WorkOrder } from '@atom/types/work';
import { hasAccess } from '@atom/utilities/accessUtilities';

import SidebarMessage from '../shared/SidebarMessage';
import { getSummaryImage } from '../shared/sidebarUtilities';
import TabNav from '../shared/TabNav';

import AssetInfoTab from './AssetInfoTab';
import AssetWorkTab from './AssetWorkTab';
import RenameModal from './RenameModal';
import { AttributesSavePayload } from './useAssetSidebarData';

import sidebarClasses from '../shared/sidebars.module.css';

const TABS: { value: string; icon: IconComponent }[] = [
  { value: 'info', icon: InfoIcon },
  { value: 'work', icon: WorkIcon },
];

export type AssetSidebarViewProps = {
  asset: AssetDetail | undefined;
  workOrders: WorkOrder[];
  loading: boolean;
  error?: boolean;
  loadingRename: boolean;
  loadingApprove: boolean;
  attributeLoading: string[];
  onClose: () => void;
  onViewDetail?: () => void;
  onRename: (name: string) => void;
  onApproveAllChanges: () => Promise<boolean>;
  onAttributesPendingChange: (
    action: string,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => Promise<void>;
  onAttributesSave: (payload: AttributesSavePayload) => Promise<void>;
};

const AssetSidebarView = ({
  asset,
  workOrders,
  loading,
  error,
  loadingRename,
  loadingApprove,
  attributeLoading,
  onClose,
  onViewDetail,
  onRename,
  onApproveAllChanges,
  onAttributesPendingChange,
  onAttributesSave,
}: AssetSidebarViewProps) => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const [renameOpen, setRenameOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [, { Link }] = useNavigation();

  if (loading) {
    return (
      <div className={sidebarClasses.spinnerContainer}>
        <Progress />
      </div>
    );
  }

  if (error) {
    return (
      <SidebarMessage
        icon={ErrorOutlineIcon}
        message="Something went wrong loading this asset."
        onClose={onClose}
        variant="error"
      />
    );
  }

  if (!asset) {
    return (
      <SidebarMessage
        icon={SearchOffIcon}
        message="This asset was not found."
        onClose={onClose}
      />
    );
  }

  const canRename = hasAccess(PolicyAction.RENAME, asset.actions);
  const canManageChanges = hasAccess(
    PolicyAction.MANAGE_INVENTORY_CHANGES,
    asset.actions,
  );
  const showApproveChanges = canManageChanges && asset.changeType;

  const imageUrl = getSummaryImage(asset.mainPhotoFileId, asset.location, 340);

  const handleApproveAll = async () => {
    const succeeded = await onApproveAllChanges();

    if (succeeded) {
      setApproveOpen(false);
    }
  };

  return (
    <>
      <div className={sidebarClasses.container}>
        <div className={sidebarClasses.header}>
          <div className={sidebarClasses.headerTitleRow}>
            <div className={sidebarClasses.headerTitle}>
              {loadingRename ? <Progress size={20} /> : asset.name}
            </div>
            <div className={sidebarClasses.headerActions}>
              {showApproveChanges && (
                <IconButton
                  tooltip="Approve All Changes"
                  onClick={() => setApproveOpen(true)}
                >
                  <Icon icon={DoneAllIcon} />
                </IconButton>
              )}
              {canRename && (
                <IconButton
                  tooltip="Rename"
                  onClick={() => setRenameOpen(true)}
                >
                  <Icon>
                    <img src={renameIcon} />
                  </Icon>
                </IconButton>
              )}
              <IconButton tooltip="Close" onClick={onClose}>
                <Icon icon={CloseIcon} />
              </IconButton>
            </div>
          </div>
        </div>
        <div className={sidebarClasses.trayContentContainer}>
          <div
            className={sidebarClasses.summaryImage}
            style={{ backgroundImage: `url('${imageUrl}')` }}
          />
          <TabNav tabs={TABS} active={activeTab} onSelect={setActiveTab} />
          {activeTab === 'info' ? (
            <AssetInfoTab
              asset={asset}
              attributeLoading={attributeLoading}
              onAttributesPendingChange={onAttributesPendingChange}
              onAttributesSave={onAttributesSave}
            />
          ) : (
            <AssetWorkTab asset={asset} workOrders={workOrders} />
          )}
        </div>
        {onViewDetail ? (
          <div
            className={sidebarClasses.viewDetailButton}
            onClick={onViewDetail}
          >
            View Full Detail
          </div>
        ) : (
          <Link to={`/inventory/${asset.id}`}>
            <div className={sidebarClasses.viewDetailButton}>
              View Full Detail
            </div>
          </Link>
        )}
      </div>
      <Modal
        open={approveOpen}
        loading={loadingApprove}
        cancelButtonText="Cancel"
        confirmButtonText="Approve All"
        onCancel={() => setApproveOpen(false)}
        onConfirm={handleApproveAll}
        title="Approve All Changes"
      >
        {`Are you sure you want to approve all changes to ${asset.name}?`}
      </Modal>
      <RenameModal
        key={asset.id}
        open={renameOpen}
        loading={loadingRename}
        name={asset.name}
        onCancel={() => setRenameOpen(false)}
        onSave={onRename}
      />
    </>
  );
};

export default AssetSidebarView;
